import { createSelector } from 'reselect';

import { getBodyData } from '../selectors';
import { isScreenBelow640, isScreenBelow1004 } from '../common/settings/breakpoint/selectors';

export const getArticleData = getBodyData;

/* base url */
export const getArticleBaseUrl = (state) => getArticleData(state).baseUrl;

/* review */
export const getArticleHeadline = (state) => getArticleData(state).title || '';
export const getArticles = (state) => getArticleData(state).reviews || {};
export const getArticleList = (state) => getArticles(state).list || [];
export const getUpdateMethodUrl = (state) =>
  getArticles(state).updateMethodUrl || '/shop/obzory_items/';
export const getArticleLoadingState = (state) => getArticles(state).loadingState || false;

/* review brands */
export const getBrands = (state) => getArticleData(state).reviewBrands || {};
export const getBrandsHeadline = (state) => getBrands(state).headline || '';
export const getBrandsHeadlineTagName = (state) => getBrands(state).headlineTagName || '';
export const getBrandsList = (state) => getBrands(state).list || [];
export const getBrandsListMap = createSelector(getBrandsList, (list) =>
  list.map((item) => ({
    title: item.title || '',
    url: item.url || '',
    isActive: !!item.isActive,
  })),
);
export const getBrandsActiveSlugs = createSelector(getBrandsList, (list) => {
  const activeList = list.filter((item) => item.isActive);
  return activeList.map((item) => item.slug);
});

/* review collections */
export const getCollections = (state) => getArticleData(state).reviewCollections || {};
export const getCollectionsActiveSlug = (state) => getCollections(state).activeSlug || '';
export const getCollectionsList = (state) => getCollections(state).list || [];

export const getCollectionsListMap = createSelector(
  getCollectionsList,
  getCollectionsActiveSlug,
  getBrandsList,
  (list, activeSlug, brandsList) => {
    // --- for seo task SHOP-4789 (replace url in collection)
    const activeBrands = brandsList.filter((brand) => brand.isActive);

    let listMap = [];
    if (activeBrands.length > 0) {
      const activeBrandSlug = activeBrands[0].slug || null;

      listMap = list.map((item) => {
        const subList = (item.collections || {}).list || [];
        const brandSubItems = subList.filter((subItem) => subItem.slug === activeBrandSlug);
        const targetUrl = brandSubItems.length > 0 ? brandSubItems[0].url : item.url;
        return {
          title: item.title || '',
          url: targetUrl,
          isActive: activeSlug === item.slug,
        };
      });
    } else {
      listMap = list.map((item) => ({
        title: item.title || '',
        url: item.url || '',
        isActive: activeSlug === item.slug,
      }));
    }
    // ---

    return listMap;
  },
);

export const getSubCollectionsListMap = createSelector(
  getCollectionsList,
  getCollectionsActiveSlug,
  (list, slug) => {
    let subCollectionsListMap = [];

    const activeCollections = list.filter((item) => item.slug === slug);
    const activeCollection = activeCollections.length > 0 ? activeCollections[0] : {};

    const activeCollectionList = (activeCollection.collections || {}).list || [];
    const activeCollectionActiveSlug = (activeCollection.collections || {}).activeSlug || null;

    subCollectionsListMap = activeCollectionList.map((item) => ({
      title: item.title || '',
      url: item.url || '',
      isActive: item.slug === activeCollectionActiveSlug,
    }));

    return subCollectionsListMap;
  },
);

export const getSubCollectionsActiveSlug = createSelector(
  getCollectionsList,
  getCollectionsActiveSlug,
  (list, slug) => {
    const activeCollections = list.filter((item) => item.slug === slug);
    const activeCollection = activeCollections.length > 0 ? activeCollections[0] : {};

    return (activeCollection.collections || {}).activeSlug || null;
  },
);

/* review tags */
export const getTags = (state) => getArticleData(state).reviewTags || {};
export const getTagsHeadline = (state) => getTags(state).headline || '';
export const getTagsHeadlineTagName = (state) => getTags(state).headlineTagName || '';
export const getTagsList = (state) => getTags(state).list || [];
export const getTagsListMap = createSelector(getTagsList, (list) =>
  list.map((item) => ({
    title: item.title || '',
    url: item.url || '',
    isActive: !!item.isActive,
  })),
);
export const getTagsActiveSlugs = createSelector(getTagsList, (list) => {
  const activeList = list.filter((item) => item.isActive);
  return activeList.map((item) => item.slug);
});

/* paging */
export const getArticlePagingData = (state) => getArticleData(state).paging || {};
export const getArticlePagingCount = (state) => getArticlePagingData(state).count || 1;
export const getArticlePagingCurrentPage = (state) => getArticlePagingData(state).page || 1;
export const getArticlePagingSize = (state) => getArticlePagingData(state).size || 1;
export const getArticlePagingTotal = createSelector(
  getArticlePagingCount,
  getArticlePagingSize,
  (count, size) => Math.ceil(count / size),
);
export const getArticlePagingVisible = createSelector(
  getArticlePagingCount,
  getArticlePagingSize,
  (count, size) => count > size,
);

export const getArticlePagingUrls = createSelector(
  getArticlePagingSize,
  getArticleBaseUrl,
  (size, baseUrl) => (page) => {
    const skip = page > 1 ? `?skip=${size * (page - 1)}` : '';
    return `${baseUrl}${skip}`;
  },
);

/** block of popular articles */
export const getPopularArticles = (state) => getArticleData(state).popular || {};
export const getPopularArticlesList = (state) => getPopularArticles(state).list || [];
export const getPopularArticlesTags = (state) => getArticleData(state).popularTags || {};

/**
 * const for delegative class set tablet = 768 < width < 1004, mobile = width < 768
 */
export const getSizeModel = createSelector(
  isScreenBelow1004,
  isScreenBelow640,
  (below1004, below640) => ({ mobile: below640, tablet: below1004 && !below640 }),
);
/**
 * const for delegative framesPerSlide set 1 = width < 768, 2 = 768 < width < 1004, 3 = width >= < 1004
 */
export const getFramePerSlideRate = createSelector(getSizeModel, ({ mobile }) => {
  const defaultFrames = 3;
  if (mobile) return 1;
  return defaultFrames;
});
